import * as React from "react";
import {
  FloatingFocusManager,
  FloatingList,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  flip,
  limitShift,
  offset,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree,
  useHover,
  useInteractions,
  useListItem,
  useListNavigation,
  useMergeRefs,
  useRole,
  type Placement,
} from "@floating-ui/react";
import { useIcon } from "@salt-ds/core";

import { useControlled } from "../forms/useControlled";
import { mergeProps } from "../utils/mergeProps";
import { usePortalScopeClassName } from "./usePortalScope";
import "./menu.css";

/**
 * The Menu family — Menu, MenuTrigger, MenuPanel, MenuItem, MenuGroup — replacing
 * `@salt-ds/core`'s behind `dropdown-menu.tsx`.
 *
 * ── SUBMENUS ARE PORTED EVEN THOUGH THIS APP HAS NONE ─────────────────────────────────
 *
 * All ten call sites use flat menus, and a flat-only port would be smaller and would pass every
 * test here. It would also silently narrow the API, and the failure would surface much later as
 * a submenu that renders but never opens. `@floating-ui/react` supplies the whole nesting
 * apparatus — `FloatingTree`, `FloatingNode`, parent-node ids — so keeping it costs structure
 * rather than invention.
 *
 * ── WHY A TREE AT ALL ─────────────────────────────────────────────────────────────────
 *
 * A nested menu is a separate floating element. Without a tree, dismissing a child leaves its
 * parent open, choosing an item three levels down closes only its own panel, and moving the
 * pointer diagonally from a parent item toward its submenu closes that submenu on the way. The
 * tree is what makes those one interaction: `tree.events.emit("click")` closes the whole chain,
 * and `safePolygon` holds the submenu open while the pointer crosses the gap.
 *
 * ── ENTER AND SPACE ARE FORWARDED AS A SYNTHETIC CLICK ────────────────────────────────
 *
 * A menu item is a `div[role="menuitem"]`, not a button, so the browser does not turn Enter or
 * Space into a click. Without that forwarding a menu is operable by pointer only — the most
 * common way a custom menu fails a keyboard user. It is dispatched in a microtask so the key
 * handler finishes first, and skipped for a submenu trigger, where Enter means "open".
 */
type PanelPosition = {
  top: number;
  left: number;
  position: "absolute" | "fixed";
  width?: number;
  height?: number;
};

interface MenuContextValue {
  openState: boolean;
  isNested: boolean;
  activeIndex: number | null;
  focusInside: boolean;
  setFocusInside: (v: boolean) => void;
  setTriggerDisabled: (v: boolean) => void;
  elementsRef: React.MutableRefObject<Array<HTMLElement | null>>;
  context: ReturnType<typeof useFloating>["context"];
  refs: ReturnType<typeof useFloating>["refs"];
  referenceEl: Element | null;
  getReferenceProps: (p?: Record<string, unknown>) => Record<string, unknown>;
  getFloatingProps: (p?: Record<string, unknown>) => Record<string, unknown>;
  getItemProps: (p?: Record<string, unknown>) => Record<string, unknown>;
  getPanelPosition: () => PanelPosition;
}

const MenuContext = React.createContext<MenuContextValue | null>(null);

/**
 * Required by the parts that CANNOT work without a Menu: the panel has no open state and no
 * position outside one, so it would render detached at 0,0 and never close. A loud failure beats
 * a surface that is silently inert.
 */
function useMenuContext(): MenuContextValue {
  const context = React.useContext(MenuContext);
  if (!context) throw new Error("Menu parts must be rendered inside a <Menu>.");
  return context;
}

/**
 * `MenuItem` uses the OPTIONAL form, and that distinction is not cosmetic.
 *
 * An item reads the context for one thing — telling a parent submenu trigger that it is
 * disabled — and it renders perfectly well without it: a `div[role="menuitem"]` with its own
 * label and click handler. Existing tests render items standalone to check exactly that, and
 * making this throw took ~300 unrelated tests down with it.
 *
 * So the rule is: a part throws when the missing provider would make it BROKEN, and copes when
 * the missing provider only makes it less connected.
 */
function useOptionalMenuContext(): Pick<MenuContextValue, "setTriggerDisabled"> {
  const context = React.useContext(MenuContext);
  return context ?? { setTriggerDisabled: () => undefined };
}

interface MenuPanelContextValue {
  activeIndex: number | null;
  getItemProps: (p?: Record<string, unknown>) => Record<string, unknown>;
  setFocusInside: (v: boolean) => void;
}

const MenuPanelContext = React.createContext<MenuPanelContextValue>({
  activeIndex: null,
  getItemProps: (p) => p ?? {},
  setFocusInside: () => undefined,
});

const MenuTriggerContext = React.createContext<{ triggersSubmenu: boolean; blurActive: boolean }>({
  triggersSubmenu: false,
  blurActive: false,
});

export interface MenuProps {
  children?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
}

function MenuBase({ children, defaultOpen, onOpenChange, open, placement }: MenuProps) {
  const parentId = useFloatingParentNodeId();
  const nodeId = useFloatingNodeId();
  const tree = useFloatingTree();
  const elementsRef = React.useRef<Array<HTMLElement | null>>([]);
  const isNested = parentId != null;

  const [openState, setOpenState] = useControlled<boolean>({
    controlled: open,
    default: Boolean(defaultOpen),
  });
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };

  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [focusInside, setFocusInside] = React.useState(false);
  const [triggerDisabled, setTriggerDisabled] = React.useState(false);
  const [referenceEl, setReferenceEl] = React.useState<Element | null>(null);

  const { context, elements, refs, x, y, strategy } = useFloating({
    nodeId,
    open: openState,
    onOpenChange: setOpen,
    // A submenu opens beside its parent item; a root menu opens under its trigger.
    placement: placement ?? (isNested ? "right-start" : "bottom-start"),
    middleware: [
      offset(isNested ? { crossAxis: -1, mainAxis: 2 } : 1),
      flip({}),
      shift({ limiter: limitShift() }),
      // Caps the panel at the space actually available, so a long menu scrolls instead of
      // running off the bottom of the window where its last items cannot be reached.
      size({
        apply({ elements: sized, availableHeight }: { elements: { floating: HTMLElement }; availableHeight: number }) {
          Object.assign(sized.floating.style, { maxHeight: `${availableHeight}px` });
        },
      }),
    ],
  });

  const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions([
    // Hover opens a SUBMENU only. `safePolygon` keeps it open while the pointer travels
    // diagonally toward it, across the gap where it is over neither element.
    useHover(context, {
      enabled: isNested && !focusInside && !triggerDisabled,
      handleClose: safePolygon({ blockPointerEvents: true }),
    }),
    useClick(context, { event: "mousedown", toggle: !isNested, ignoreMouse: isNested }),
    useRole(context, { role: "menu" }),
    // `bubbles` so Escape inside a submenu closes the whole chain rather than one level of it.
    useDismiss(context, { bubbles: true }),
    useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      nested: isNested,
      onNavigate: setActiveIndex,
    }),
  ]);

  React.useEffect(() => {
    if (!tree) return;
    const handleItemClick = () => setOpen(false);
    tree.events.on("click", handleItemClick);
    return () => tree.events.off("click", handleItemClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree]);

  const getPanelPosition = (): PanelPosition => ({
    top: y ?? 0,
    left: x ?? 0,
    position: strategy,
    width: elements.floating?.offsetWidth,
    height: elements.floating?.offsetHeight,
  });

  /*
   * Not memoised — the same call as the radio group's context. The handlers here close over the
   * caller's `onOpenChange`, and pinning that froze a handler earlier in this migration and
   * silently dropped state. The cost is re-rendering the trigger and the panel.
   */
  const value: MenuContextValue = {
    openState,
    isNested,
    activeIndex,
    focusInside,
    setFocusInside,
    setTriggerDisabled,
    elementsRef,
    context,
    refs,
    referenceEl,
    getReferenceProps: (p) => ({
      ...getReferenceProps(p),
      ref: (node: HTMLElement | null) => {
        // Tracked in state so the portal scope can read the trigger's provider ancestor.
        setReferenceEl(node);
        refs.setReference(node);
      },
    }),
    getFloatingProps,
    getItemProps,
    getPanelPosition,
  };

  return (
    <FloatingNode id={nodeId}>
      <MenuContext.Provider value={value}>{children}</MenuContext.Provider>
    </FloatingNode>
  );
}

export function Menu(props: MenuProps) {
  const parentId = useFloatingParentNodeId();
  // The outermost menu owns the tree; a nested one joins the tree it is already inside.
  if (parentId === null) {
    return (
      <FloatingTree>
        <MenuBase {...props} />
      </FloatingTree>
    );
  }
  return <MenuBase {...props} />;
}

export interface MenuTriggerProps {
  children?: React.ReactNode;
}

export function MenuTrigger({ children, ...rest }: MenuTriggerProps) {
  const { focusInside, getReferenceProps, openState, setFocusInside } = useMenuContext();
  const { setFocusInside: setFocusInsideParent } = React.useContext(MenuPanelContext);

  if (!children || !React.isValidElement(children)) return <>{children}</>;

  return (
    <MenuTriggerContext.Provider
      value={{ triggersSubmenu: true, blurActive: focusInside && openState }}
    >
      {/* MERGED, not spread — see ../utils/mergeProps. A child with its own onClick would
          otherwise replace the handler that opens the menu. */}
      {React.cloneElement(children, {
        ...mergeProps(
          getReferenceProps({
            onFocus() {
              setFocusInsideParent(true);
              setFocusInside(false);
            },
            ...rest,
          }),
          children.props as Record<string, unknown>,
        ),
      } as React.HTMLAttributes<HTMLElement>)}
    </MenuTriggerContext.Provider>
  );
}

export type MenuPanelProps = React.HTMLAttributes<HTMLDivElement>;

export const MenuPanel = React.forwardRef<HTMLDivElement, MenuPanelProps>(function MenuPanel(
  { children, className, ...rest },
  ref,
) {
  const {
    activeIndex,
    context,
    elementsRef,
    getFloatingProps,
    getItemProps,
    getPanelPosition,
    isNested,
    openState,
    referenceEl,
    refs,
    setFocusInside,
  } = useMenuContext();
  const mergedRef = useMergeRefs([refs.setFloating, ref]);
  const portalScope = usePortalScopeClassName(referenceEl);

  if (!openState) return null;
  /*
   * ONLY position is applied inline. `getPanelPosition()` also reports the panel's measured
   * width and height for API parity, and writing those back as styles would be circular —
   * measuring the panel in order to size itself, which pins it to whatever it happened to be on
   * the first frame instead of letting it size to its content.
   *
   * `dropdown-menu.test.tsx` asserts `menu.style.width === ""` directly, which is how the first
   * attempt at this was caught: in jsdom every measurement is 0, so the panel shipped a literal
   * `width: 0px`.
   */
  const { left, position, top } = getPanelPosition();

  return (
    <MenuPanelContext.Provider value={{ activeIndex, getItemProps, setFocusInside }}>
      <FloatingList elementsRef={elementsRef}>
        <FloatingPortal>
          <div className={portalScope}>
            {/*
              `initialFocus: -1` and `returnFocus: false` for a submenu: focus stays on the
              parent item that opened it, and returning focus on close would drag it out of the
              chain the user is still navigating.
            */}
            <FloatingFocusManager
              context={context}
              initialFocus={isNested ? -1 : 0}
              modal={false}
              returnFocus={!isNested}
            >
              <div
                className={className ? `saltMenuPanel ${className}` : "saltMenuPanel"}
                role="menu"
                {...getFloatingProps()}
                ref={mergedRef}
                style={{ top, left, position }}
                tabIndex={-1}
                {...rest}
              >
                <div>{children}</div>
              </div>
            </FloatingFocusManager>
          </div>
        </FloatingPortal>
      </FloatingList>
    </MenuPanelContext.Provider>
  );
});

export interface MenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
}

export const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(function MenuItem(
  { children, className, disabled, onClick, onFocus, onKeyDown, ...rest },
  ref,
) {
  const { blurActive, triggersSubmenu } = React.useContext(MenuTriggerContext);
  /*
   * Read through Salt's semantic icon registry rather than importing ChevronRightIcon directly.
   * `ExpandGroupIcon` resolves to ChevronRightIcon by default, but the registry is overridable —
   * an app that swaps its icon set expects the submenu chevron to swap with it, and a hard
   * import would quietly opt this one component out. The icon layer is deliberately still Salt's.
   */
  const { ExpandGroupIcon } = useIcon();
  const { setTriggerDisabled } = useOptionalMenuContext();
  const { activeIndex, getItemProps, setFocusInside } = React.useContext(MenuPanelContext);
  const item = useListItem();
  const tree = useFloatingTree();
  const active = item.index === activeIndex;
  const mergedRef = useMergeRefs([item.ref, ref]);

  React.useEffect(() => {
    setTriggerDisabled(!!disabled);
  }, [disabled, setTriggerDisabled]);

  const classes = ["saltMenuItem"];
  if (blurActive) classes.push("saltMenuItem-blurActive");
  if (className) classes.push(className);

  return (
    <div
      aria-disabled={disabled || undefined}
      className={classes.join(" ")}
      role="menuitem"
      {...getItemProps({
        // Roving tabindex: exactly one item is tabbable, and the arrow keys move which one.
        tabIndex: disabled ? undefined : active ? 0 : -1,
        onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
          const element = event.currentTarget;
          onKeyDown?.(event);
          if ((event.key === " " || event.key === "Enter") && !triggersSubmenu && !disabled) {
            event.preventDefault();
            const { view, ...eventInit } = event as unknown as { view: unknown };
            // A microtask, so this key handler completes before the click is delivered.
            queueMicrotask(() => {
              element.dispatchEvent(new MouseEvent("click", eventInit as MouseEventInit));
            });
            tree?.events.emit("click");
          }
        },
        onClick(event: React.MouseEvent<HTMLDivElement>) {
          if (disabled) return;
          onClick?.(event);
          // Closes the WHOLE chain, not just this panel. A submenu trigger is excluded, because
          // clicking it is navigation rather than a choice.
          if (!triggersSubmenu) tree?.events.emit("click");
        },
        onFocus(event: React.FocusEvent<HTMLDivElement>) {
          onFocus?.(event);
          setFocusInside(true);
        },
        ...rest,
      })}
      ref={mergedRef}
    >
      {children}
      {triggersSubmenu ? <ExpandGroupIcon aria-hidden className="saltMenuItem-expandIcon" /> : null}
    </div>
  );
});

export interface MenuGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: React.ReactNode;
}

export const MenuGroup = React.forwardRef<HTMLDivElement, MenuGroupProps>(function MenuGroup(
  { children, className, label, ...rest },
  ref,
) {
  const labelId = React.useId();
  return (
    <div
      aria-labelledby={label ? labelId : undefined}
      className={className ? `saltMenuGroup ${className}` : "saltMenuGroup"}
      ref={ref}
      role="group"
      {...rest}
    >
      {/*
        `aria-hidden` on the visible label: the group is already NAMED by it through
        `aria-labelledby`, so leaving it readable makes a screen reader announce the heading
        twice — once as the group's name, again as an item inside it.
      */}
      {label ? (
        <div aria-hidden className="saltMenuGroup-label" id={labelId}>
          {label}
        </div>
      ) : null}
      {children}
    </div>
  );
});
